import isEmpty from 'lodash/isEmpty';

import { VARIANTS } from '@jobcase/react_pattern_library/components/Toaster/Toaster';

import entity from './entity';
import { setNotification } from '../../notification/actions';
import { getProfileKey } from '../../authentication/selectors';
import { omit } from '../../../../utils/utils';

const TYPES = {
  ...entity.getTypes(),
  CLEAR_NEW_JOB_ID: `${entity.name}/CLEAR_NEW_JOB_ID`,
  CLEAR_STATE: `${entity.name}/CLEAR_STATE`,
  CREATE_JOB_FAILURE: `${entity.name}/CREATE_JOB_FAILURE`,
  CREATE_JOB_REQUEST: `${entity.name}/CREATE_JOB_REQUEST`,
  CREATE_JOB_SUCCESS: `${entity.name}/CREATE_JOB_SUCCESS`,
  GET_JOBS_FAILURE: `${entity.name}/GET_JOBS_FAILURE`,
  GET_JOBS_REQUEST: `${entity.name}/GET_JOBS_REQUEST`,
  GET_JOBS_SUCCESS: `${entity.name}/GET_JOBS_SUCCESS`,
  INCREMENT_TOTAL_JOBS: `${entity.name}/INCREMENT_TOTAL_JOBS`,
  JOB_TITLE_SEARCH_FAILURE: `${entity.name}/JOB_TITLE_SEARCH_FAILURE`,
  JOB_TITLE_SEARCH_REQUEST: `${entity.name}/JOB_TITLE_SEARCH_REQUEST`,
  JOB_TITLE_SEARCH_SUCCESS: `${entity.name}/JOB_TITLE_SEARCH_SUCCESS`,
};

export const getJob = options => async (dispatch) => {
  dispatch({ type: TYPES.GET_JOBS_REQUEST, payload: { page: 1 } });
  try {
    const response = await entity.api.getJob(options);
    const data = response?.data;
    const jobId = options?.jobId;
    dispatch({ type: TYPES.GET_REQUEST_SUCCESS, payload: { [jobId]: data } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.GET_REQUEST_ERROR, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading the job. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while loading the job.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const createJob = data => async (dispatch) => {
  dispatch({ type: TYPES.CREATE_JOB_REQUEST });
  try {
    const response = await entity.api.createJob(data);
    const jobData = response?.data;
    dispatch({ type: TYPES.CREATE_JOB_SUCCESS, payload: { [jobData.id]: jobData, id: jobData.id } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.CREATE_JOB_FAILURE, payload: error });

    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while creating job. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while creating job.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const updateJob = data => async (dispatch) => {
  dispatch({ type: TYPES.UPDATE_REQUEST });
  try {
    const response = await entity.api.updateJob(data);
    const job = response?.data;
    dispatch({ type: TYPES.UPDATE_REQUEST_SUCCESS, payload: { [job.id]: job } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.UPDATE_REQUEST_ERROR, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while updating the job. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while updating the job.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const getAllJobs = options => async (dispatch, getState) => {
  const newOptions = { ...options };
  if (newOptions?.is_active?.length === 2) delete newOptions.is_active;
  dispatch({ type: TYPES.GET_JOBS_REQUEST, payload: { page: newOptions.page_current } });
  const profileKey = getProfileKey(getState());
  try {
    const response = await entity.api.getJobs(newOptions, profileKey);
    const { data: { content, pagination } } = response;
    const data = content.reduce((result, entity) => ({ ...result, [entity.id]: entity }), {});
    dispatch({ type: TYPES.GET_JOBS_SUCCESS, payload: { content: data, pagination } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.GET_JOBS_FAILURE, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading jobs. ${error.errors[0]}`, VARIANTS.INFO));
    } else if (error.error_code !== 403) {
      dispatch(setNotification('An error occurred while loading jobs.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const getAllJobsSortedByCandidatesCount = options => async (dispatch, getState) => {
  const organizationId = options.organization_id;
  const newOptions = { ...options };
  if (newOptions?.is_active?.length === 2) delete newOptions.is_active;
  dispatch({ type: TYPES.GET_JOBS_REQUEST, payload: { page: newOptions.page_current } });
  const profileKey = getProfileKey(getState());
  try {
    const candidatesCountResponse = await entity.api.getJobsCandidatesCount(organizationId, options);
    const jobIds = (candidatesCountResponse?.data?.content || []).map(obj => obj.id);
    if (!jobIds.length) {
      dispatch({ type: TYPES.GET_JOBS_SUCCESS, payload: { content: {}, pagination: candidatesCountResponse.data.pagination } });
      return candidatesCountResponse;
    }
    newOptions.job_id = [...(newOptions.jobId || []), ...jobIds];
    const response = await entity.api.getJobs(omit(['page_current'], newOptions), profileKey);
    const { data: { content } } = response;
    const data = content.reduce((result, entity) => ({ ...result, [entity.id]: entity }), {});
    dispatch({ type: TYPES.GET_JOBS_SUCCESS, payload: { content: data, pagination: candidatesCountResponse.data.pagination } });
    return candidatesCountResponse;
  } catch (error) {
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading jobs. ${error.errors[0]}`, VARIANTS.INFO));
    } else if (error.error_code !== 403) {
      dispatch(setNotification('An error occurred while loading jobs.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const getJobTitleSearch = options => async (dispatch, getState) => {
  dispatch({ type: TYPES.JOB_TITLE_SEARCH_REQUEST });
  const profileKey = getProfileKey(getState());
  try {
    const response = await entity.api.getJobs(options, profileKey);
    const { data: { content } } = response;
    const data = content.reduce((result, entity) => ({ ...result, [entity.id]: entity }), {});
    dispatch({ type: TYPES.JOB_TITLE_SEARCH_SUCCESS, payload: data });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.JOB_TITLE_SEARCH_FAILURE, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading jobs. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while loading jobs.', VARIANTS.INFO));
    }
    throw error;
  }
};

export const clearState = () => (dispatch) => {
  dispatch({ type: TYPES.CLEAR_STATE });
};

export const incrementTotalJobsCount = () => ({ type: TYPES.INCREMENT_TOTAL_JOBS });

export const clearNewJobId = () => ({ type: TYPES.CLEAR_NEW_JOB_ID });

export {
  TYPES,
};

export default {
  TYPES,
  ...entity.getActions(),
  clearNewJobId,
  clearState,
  createJob,
  getAllJobs,
  getJob,
  getJobTitleSearch,
  incrementTotalJobsCount,
  updateJob,
  getAllJobsSortedByCandidatesCount,
};
